import { ExternalEventResponseState } from '@griply/types';
import { mix, rgba, setLightness } from 'polished';
import { schemeColor } from 'shared/assets/styles/scheme-color.style';
import { ColorScheme } from 'shared/assets/styles/theme/color-schemes';
import { Badge } from 'shared/components/ui/calendar-event-sync-badge/calendar-event-sync-badge.style';
import { typographyToCss } from 'shared/lib/mui';
import styled, { css, DefaultTheme } from 'styled-components';

// Tonal palette: text and icons pin a fixed lightness per element per mode, so a light color can't blow out to white.
const DARK_CARD_TINT_WEIGHT = 0.22;

// The dark fill blends toward the page instead: at a fixed HSL lightness, blue landed on the page's exact luminance.
const getCardBackground = (theme: DefaultTheme, color?: string | null) => {
  if (!color) {
    return theme.palette.grey[100];
  }

  return theme.colorScheme === ColorScheme.Dark
    ? mix(
        DARK_CARD_TINT_WEIGHT,
        schemeColor(color, theme),
        theme.palette.background.default,
      )
    : setLightness(0.95, schemeColor(color, theme));
};

const getTitleColor = (theme: DefaultTheme, color?: string | null) => {
  if (!color) {
    return theme.palette.text.primary;
  }

  return theme.colorScheme === ColorScheme.Dark
    ? setLightness(0.85, schemeColor(color, theme))
    : setLightness(0.3, schemeColor(color, theme));
};

const getTimeColor = (theme: DefaultTheme, color?: string | null) => {
  if (!color) {
    return theme.palette.text.secondary;
  }

  return theme.colorScheme === ColorScheme.Dark
    ? setLightness(0.8, schemeColor(color, theme))
    : setLightness(0.35, schemeColor(color, theme));
};

export const Container = styled.div<{
  $color?: string | null;
  $outlined?: boolean;
}>`
  position: relative;
  width: 100%;
  height: 100%;

  border-radius: ${({ theme }) => theme.spacing(1.5)};
  background: ${({ theme, $color }) => getCardBackground(theme, $color)};
  box-shadow: 0 0 0 1px ${({ theme }) => theme.palette.background.default};
  overflow: hidden;

  /* Inset, because the card clips to its own radius and an outer ring would be cut off. */
  ${({ $outlined, $color, theme }) =>
    $outlined &&
    css`
      box-shadow:
        0 0 0 1px ${theme.palette.background.default},
        inset 0 0 0 1px
          ${$color
            ? rgba(schemeColor($color, theme), 0.5)
            : theme.palette.grey[400]};

      background: repeating-linear-gradient(
          to bottom right,
          ${theme.palette.grey[100]} 0 0.2rem,
          transparent 0.2rem 0.5rem
        ),
        ${theme.palette.background.default};
    `};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};

  color: ${({ theme }) => theme.palette.grey[600]};
  text-decoration: none;

  /* All-day events (daygrid) are text-only and content-height — keep them from
     collapsing below the timed-event icon height. */
  .fc-daygrid-event & {
    min-height: 2rem;
    display: flex;
    align-content: center;
  }

  /* The sync badge for a task that isn't syncing yet is an opt-in affordance —
     it stays hidden until the card is hovered so it doesn't add an icon to
     every event. Synced badges set their own opacity and ignore this. */
  &:hover ${Badge} {
    opacity: 1;
  }
`;

// External calendar events use a thin color bar as their leading indicator. The
// bar hugs its own width (it doesn't reserve an icon-sized slot) and fills the
// card height (min 2rem).
export const ColorBarSlot = styled.span<{
  $color?: string | null;
  $past?: boolean;
  $responseState?: ExternalEventResponseState;
}>`
  align-self: stretch;
  min-height: 2rem;
  width: ${({ theme }) => theme.spacing(1)};
  margin-right: ${({ theme }) => theme.spacing(1)};

  background: ${({ $color }) => $color ?? 'transparent'};
  opacity: ${({ $past }) => ($past ? 0.5 : 1)};

  ${({ $responseState, $color }) =>
    $responseState === ExternalEventResponseState.Tentative &&
    css`
      background: repeating-linear-gradient(
        135deg,
        ${$color ?? 'transparent'} 0 0.2rem,
        transparent 0.2rem 0.4rem
      );
    `};
`;

export const Content = styled.div`
  position: relative;
  display: flex;
  gap: ${({ theme }) => theme.spacing(0.25)};
  align-items: flex-start;

  width: 100%;
  height: 100%;

  /* Center the icon/checkbox vertically only on all-day events (rendered as
     daygrid) and on short timed events; tall timed events keep top alignment. */
  .fc-daygrid-event &,
  .fc-timegrid-event-short & {
    align-items: center;
  }
`;

export const MetaContainer = styled.div`
  font-size: 1.6rem;
`;

export const LeadingSlot = styled.span<{
  $color: string | undefined;
  $completed?: boolean;
  $past?: boolean;
}>`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  align-self: stretch;
  min-height: 2.2rem;
  width: 2.2rem;
  padding: ${({ theme }) => theme.spacing(1.25)};

  font-size: 1.2rem;
  line-height: 62.5%;
  color: ${({ $color, theme }) =>
    $color ? schemeColor($color, theme) : 'inherit'};
  opacity: ${({ $completed, $past }) => ($completed || $past ? 0.5 : 1)};
`;

// Dims the slot, not the badge: the badge's own opacity is its hover reveal.
export const TrailingSlot = styled.span<{ $completed?: boolean }>`
  display: flex;
  /* TextColumn absorbs no shrinkage, so the slot would take all of it. */
  flex-shrink: 0;
  opacity: ${({ $completed }) => ($completed ? 0.5 : 1)};

  /* The badge often renders nothing; an empty slot would still claim a gap. */
  &:empty {
    display: none;
  }

  /* The hover reveal has to land at full strength, not 0.5 of it. */
  ${Container}:hover & {
    opacity: 1;
  }
`;

export const Title = styled.span<{
  $completed: boolean;
  $numberOfLines?: number;
  $color?: string | null;
  $past?: boolean;
}>`
  position: relative;
  ${({ theme }) => typographyToCss(theme.typography.buttonSmall)};
  flex: 1;
  font-weight: 500;

  overflow: hidden;

  color: ${({ theme, $color }) => getTitleColor(theme, $color)};
  line-height: 1.2;
  word-wrap: break-word;

  display: -webkit-box;
  -webkit-line-clamp: ${({ $numberOfLines }) => $numberOfLines ?? 1};
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;

  ${({ $completed }) =>
    $completed &&
    css`
      text-decoration: line-through;
    `};

  ${({ $completed, $past }) =>
    ($completed || $past) &&
    css`
      opacity: 0.5;
    `};
`;

export const TextColumn = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  gap: 1px;
  flex-direction: column;
  overflow: hidden;
  margin-top: ${({ theme }) => theme.spacing(0.75)};

  /* The top offset is only for tall timed events; all-day and short events are
     vertically centered, so drop it there. */
  .fc-daygrid-event &,
  .fc-timegrid-event-short & {
    margin-top: 0;
  }

  ${Title} {
    flex: 0 1 auto;
  }

  /* On short events FullCalendar can't fit two lines, so put the time to the
     right of the title on a single row instead of below it. */
  .fc-timegrid-event-short & {
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};

    ${Title} {
      flex: 1 1 auto;
      min-width: 0;
      margin-top: 0;
    }
  }
`;

export const TimeText = styled.span<{
  $completed?: boolean;
  $color?: string | null;
  $past?: boolean;
}>`
  ${({ theme }) => typographyToCss(theme.typography.overline)};

  overflow: hidden;

  color: ${({ theme, $color }) => getTimeColor(theme, $color)};
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.2;

  /* In the short-event row layout the title gets priority: it keeps its space
     while the time (start only, end dropped via TimeEnd) yields — shrinking
     hard and collapsing out of view — when there isn't room for both. */
  flex-shrink: 999;
  min-width: 0;

  ${({ $completed, $past }) =>
    ($completed || $past) &&
    css`
      opacity: 0.5;
    `};
`;

export const TimeEnd = styled.span`
  .fc-timegrid-event-short & {
    display: none;
  }
`;

export const DeadlineIconContainer = styled.span<{
  $color?: string;
  $completed?: boolean;
  $past?: boolean;
}>`
  display: block;

  margin: ${({ theme }) => theme.spacing(1)};

  font-size: 1.2rem;
  line-height: 1.2rem;
  opacity: ${({ $completed, $past }) => ($completed || $past ? 0.5 : 1)};

  ${({ $color, theme }) =>
    $color &&
    css`
      color: ${schemeColor($color, theme)};
    `};
`;
